import './ShareNotification.scss';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { Header } from '../Header/Header';
import { ShareType, ClientId, ISharingStore, ICustomLabeledAudienceConfig } from '../../interfaces/SharingInterfaces';
import { ShareHint, IShareHintProps } from '../ShareHint/ShareHint';
import { ShareViewState } from '../Share/Share';
import { TextField, ITextField } from 'office-ui-fabric-react/lib/TextField';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import * as ShareHelper from '../../utilities/ShareHelper';
import { PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { Callout, DirectionalHint } from 'office-ui-fabric-react/lib/Callout';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import stringsResx from '../Share.resx';

export interface IShareNotificationProps {
  shareType: ShareType;
  itemName: string;
  linkUrl: string; // The link created by the UI.
  clientId: ClientId;
  sharingHintProps: IShareHintProps;
  shouldCopyLinkToClipboard: boolean;
  customLabeledAudienceConfig?: ICustomLabeledAudienceConfig;
}

export interface IShareNotificationState {
  successfullyCopied: boolean;
  isNameTooltipVisible: boolean;
}

export class ShareNotification extends React.Component<IShareNotificationProps, IShareNotificationState> {
  private _resize: () => void;
  private _textField: ITextField;
  private _sharingStore: ISharingStore;
  private _notificationLabel: HTMLElement;

  static contextTypes = {
    resize: PropTypes.func.isRequired,
    sharingStore: PropTypes.object.isRequired
  };

  constructor(props: IShareNotificationProps, context: any) {
    super(props);

    this.state = {
      successfullyCopied: false,
      isNameTooltipVisible: false
    };

    this._resize = context.resize;
    this._sharingStore = context.sharingStore;
  }

  public componentDidMount() {
    if (this.props.shareType === ShareType.copy && this.props.shouldCopyLinkToClipboard) {
      this._copySharingLinkToClipboard();
    }
  }

  public componentDidUpdate() {
    this._resize();
  }

  public render(): React.ReactElement<{}> {
    const notificationLabel = this._getNotificationLabel();
    const nonTruncatedNotificationLabel = this._getNotificationLabel(true /* disableTruncate */);
    const notificationAriaLabel = this._getNotificationAriaLabel();
    const renderSharingLinkInformation = this.props.shareType === ShareType.copy;
    const accessRequestCreated = this._sharingStore ? this._sharingStore.hasAccessRequestBeenCreated() : false;

    /**
     * A successful share means that the link was sent (ShareType === share or moreApps)
     * or it's a copy link scenario (ShareType === copy) AND the browser was
     * able to copy to clipboard.
     */
    const shareType = this.props.shareType;
    const isSuccess = shareType === ShareType.share || shareType === ShareType.moreApps || (shareType === ShareType.copy && this.state.successfullyCopied);

    const notificationPaddingClass = isSuccess ? 'od-ShareNotification-successPadding' : 'od-ShareNotification-failPadding';

    return accessRequestCreated ?
      (
        <div className='od-ShareNotification'>
          <Header
            viewState={ ShareViewState.linkSuccess }
            clientId={ this.props.clientId }
          />
          <div className='od-ShareNotification-content ms-font-su'>
            <Icon
              iconName='PeopleAlert'
              className='od-ShareNotification-accessRequestIcon'
            />
            <div className='od-ShareNotification-accessRequestHeader ms-fontWeight-regular'>{ stringsResx.accessRequestNotificationHeader }</div>
            <div className='od-ShareNotification-accessRequestBody'>{ stringsResx.accessRequestNotificationBody }</div>
          </div>
        </div>
      ) :
      (
        <div className={ `od-ShareNotification ${notificationPaddingClass}` }>
          <Header
            viewState={ ShareViewState.linkSuccess }
            clientId={ this.props.clientId }
          />
          { this._renderCheckMark(isSuccess) }
          <div className='od-ShareNotification-content'>
            <div className='ms-font-l'
              ref={ this._onNotificationLabel }
              onMouseOver={ this._onShowNameTooltip }
              onMouseLeave={ this._onHideNameTooltip }>{ notificationLabel }</div>
            { this.state.isNameTooltipVisible && notificationLabel !== nonTruncatedNotificationLabel && (
              <Callout
                directionalHint={ DirectionalHint.topCenter }
                gapSpace={ 2 }
                onDismiss={ this._onHideNameTooltip }
                target={ this._notificationLabel }
                calloutWidth={ 300 }
                setInitialFocus={ true }
              >
                <div className="od-ShareNotification-content-tooltip">
                  { nonTruncatedNotificationLabel }
                </div>
              </Callout>
            ) }
            { renderSharingLinkInformation && this._renderCopyCta() }
            { renderSharingLinkInformation && (
              <div className='od-ShareNotification-urlText'>
                <TextField
                  autoFocus
                  aria-live='polite'
                  ariaLabel={ notificationAriaLabel }
                  onClick={ this._copySharingLinkToClipboard }
                  readOnly
                  componentRef={ (textField) => { this._textField = textField; } }
                  type='text'
                  value={ this.props.linkUrl }
                  className='od-ShareNotification-urlTextField'
                />
                <PrimaryButton
                  className='od-ShareNotification-copyButton'
                  text={ stringsResx.copyButtonLabel }
                  onClick={ this._copySharingLinkToClipboard }
                />
              </div>
            ) }
          </div>
          { renderSharingLinkInformation && this._renderShareHint() }
        </div>
      );
  }

  @autobind
  private _onShowNameTooltip() {
    this.setState({
      ...this.state,
      isNameTooltipVisible: true
    });
  }

  @autobind
  private _onHideNameTooltip() {
    this.setState({
      ...this.state,
      isNameTooltipVisible: false
    });
  }

  @autobind
  private async _copySharingLinkToClipboard() {
    ShareHelper.copySharingLinkToClipboard(this.props.clientId, this._textField, this.props.linkUrl).then(
      (successfullyCopied: boolean) => {
        // Update state with copy action result.
        this.setState({
          ...this.state,
          successfullyCopied: successfullyCopied
        });
      }
    );
  }

  @autobind
  private _onNotificationLabel(element: HTMLElement): any {
    this._notificationLabel = element;
  }

  private _renderCheckMark(isSuccess: boolean) {
    if (isSuccess) {
      return (
        <div className='od-ShareNotification-icon'>
          <Icon iconName='CheckMark' />
        </div>
      );
    }
  }

  private _renderCopyCta() {
    const props = this.props;
    const state = this.state;

    if ((props.shareType === ShareType.copy) && !state.successfullyCopied) {
      return (
        <span className='od-ShareNotification-copyCta'>{ stringsResx.notificationCopyFailedCta }</span>
      );
    }
  }

  private _renderShareHint(): JSX.Element {
    const props = this.props.sharingHintProps;

    if (props) {
      return (
        <div className='od-ShareNotification-footer'>
          <ShareHint
            clientId={ props.clientId }
            companyName={ props.companyName }
            currentSettings={ props.currentSettings }
            sharingInformation={ props.sharingInformation }
            onShareHintClick={ props.sharingInformation.requiresAccessApproval ? undefined : props.onShareHintClick }
            shouldTakeFocus={ false }
            linkRecipients={ props.linkRecipients }
            customLabeledAudienceConfig={ props.customLabeledAudienceConfig }
          />
        </div>
      );
    } else {
      return null;
    }
  }

  private _getNotificationLabel(disableTruncate?: boolean): string {
    const strings = stringsResx;
    const itemName = disableTruncate ? this.props.itemName : ShareHelper.truncateItemNameForShareNotification(this.props.itemName);
    const shareType = this.props.shareType;

    // Set notification messages.
    const copiedSuccessMessage = itemName ? StringHelper.format(strings.notificationCopied, itemName) : stringsResx.notificationCopiedLabel;
    const copiedFailureMessage = itemName ? StringHelper.format(strings.notificationCopyFailed, itemName) : stringsResx.notificationCopyFailedLabel;
    const sentMessage = itemName ? StringHelper.format(strings.notificationSent, itemName) : stringsResx.notificationSentLabel;

    // Return notification message based on flow and success.
    if (shareType === ShareType.copy) {
      return this.state.successfullyCopied ? copiedSuccessMessage : copiedFailureMessage;
    } else if (shareType === ShareType.share) {
      return sentMessage;
    } else if (shareType === ShareType.moreApps) {
      return copiedFailureMessage; /* Link to "foo" created */
    } else {
      return '';
    }
  }

  private _getNotificationAriaLabel(): string {
    const shareType = this.props.shareType;
    if (shareType === ShareType.copy) {
      return this.state.successfullyCopied ? stringsResx.notificationCopiedLabel : stringsResx.notificationCopyFailedLabel;
    } else if (shareType === ShareType.share) {
      return stringsResx.notificationSentLabel;
    } else if (shareType === ShareType.moreApps) {
      return stringsResx.notificationCopyFailedLabel;
    } else {
      return '';
    }
  }
}
